/**
 * BrandContext — Proveedor global de branding + módulos habilitados.
 *
 * - `brand.product_name`: "Amelia" (o personalizado por reseller)
 * - `brand.display_short`: nombre corto para sidebar colapsado
 * - `brand.company_name`: nombre comercial del cliente (ej. "Ferretería El Bodegón")
 * - `modules.enabled[id]`: boolean por módulo
 * - helper `isEnabled(id)` para gating condicional de UI
 *
 * Uso:
 *   const { brand, isEnabled, refresh } = useBrand();
 *   {isEnabled("pos") && <PosLink />}
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_BRAND = {
  product_name: "Amelia",
  display_short: "Amelia",
  display_tagline: "Suite de Gestión Empresarial",
  primary_color: "",
  logo_url: "",
  company_name: "",
};

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  modules: { enabled: {}, industry: "hardware", catalog: [], categories: {} },
  isEnabled: () => true,
  refresh: () => {},
  loading: true,
});

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [modules, setModules] = useState({ enabled: {}, industry: "hardware", catalog: [], categories: {} });
  const [loading, setLoading] = useState(true);
  const [loadedOk, setLoadedOk] = useState(false); // true cuando /settings/modules devolvió datos reales
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const brandRes = await api.get("/settings/branding").catch(() => null);
      const modRes = await api.get("/settings/modules").catch(() => null);
      if (brandRes?.data) setBrand({ ...DEFAULT_BRAND, ...brandRes.data });
      if (modRes?.data && modRes.data.enabled) {
        setModules(modRes.data);
        setLoadedOk(true);
      }
      const b = brandRes?.data || DEFAULT_BRAND;
      document.title = b?.company_name
        ? `${b.company_name} — ${b.product_name || "Amelia"}`
        : (b?.product_name || "Amelia");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial (puede fallar con 401 si aún no hay sesión).
  useEffect(() => { load(); }, [load]);

  // Recarga cuando el usuario autentica (resuelve el 401 inicial).
  useEffect(() => {
    if (user && typeof user === "object") load();
  }, [user, load]);

  const isEnabled = useCallback(
    (id) => {
      // Si todavía no cargó, asume true (evita parpadeo de ocultar menús)
      if (loading) return true;
      // Fail-open: si NUNCA cargamos exitosamente (p.ej. 401 persistente),
      // no escondemos todo el menú — asumimos que el módulo está habilitado.
      if (!loadedOk) return true;
      // Carga exitosa: respeta la configuración real del backend.
      return !!modules?.enabled?.[id];
    },
    [modules, loading, loadedOk],
  );

  return (
    <BrandContext.Provider value={{ brand, modules, isEnabled, refresh: load, loading }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
